<template>
  <v-row>
    <v-col v-if="exam">
      <v-row dense>
        <v-col>
          <v-combobox
            label="Exame"
            v-model="examTemplate"
            dense
            hide-details
            outlined
            :items="examTemplates"
            item-text="name"
            background-color="white"
            :disabled="
              hasMedicalRequest || signed || lodash.isEmpty(examTemplates)
            "
            @change="setTyping(true)" 
            @input="handleDescriptionChange"
          />
        </v-col>

        <v-col cols="4">
          <v-text-field
            label="Data de execução"
            v-model="executedAt"
            dense
            hide-details
            outlined
            background-color="white"
            type="datetime-local"
            :disabled="signed"
            @change="setTyping(true)" 
            @input="handleDescriptionChange"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col>
          <v-combobox
            label="Solicitante"
            v-model="requester"
            dense
            hide-details
            outlined
            :items="doctorUsers"
            item-text="employee.name"
            background-color="white"
            :disabled="hasMedicalRequest || signed"
            @change="setTyping(true)" 
            @input="handleDescriptionChange"
          />
        </v-col>

        <v-col>
          <v-combobox
            label="Executante"
            v-model="performer"
            dense
            hide-details
            outlined
            :items="doctorUsers"
            item-text="employee.name"
            background-color="white"
            :disabled="signed"
            @change="setTyping(true)" 
            @input="handleDescriptionChange"
          />
        </v-col>

        <v-col>
          <v-combobox
            label="Auxiliares"
            v-model="assistants"
            dense
            hide-details
            outlined
            :items="users"
            item-text="employee.name"
            multiple
            chips
            deletable-chips
            small-chips
            background-color="white"
            :disabled="signed"
            @change="setTyping(true)" 
            @input="handleDescriptionChange"
          />
        </v-col>
      </v-row>

      <DynamicFormCore
        v-if="dynamicFormType"
        :dynamicFormType="dynamicFormType"
        :answerableId="exam.id"
        answerableType="Exam"
        :disabled="signed"
      />

      <v-row dense>
        <v-col>
          <v-row dense class="mb-2">
            <v-col class="text-h5 gray--text"> Laudo </v-col>
            <template v-if="!disabledCheck">
              <v-col cols="auto" class="mt-1">
                <SaveStatus
                  :saving="saveManager.isSaving"
                  :pending="saveManager.pendingSave"
                  :saved-at="updatedAt"
                  :loading="examLoading"
                  :error="saveManager.isError"
                  not-saved-message="O laudo ainda não foi salvo."
                />
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="light-blue"
                  @click="saveManager.triggerManualSave()"
                  :disabled="!typing || lodash.isEmpty(examTemplate)"
                  :loading="saveManager.isSaving || examLoading"
                >
                  Salvar Laudo
                </v-btn>
              </v-col>
            </template>
          </v-row>

          <v-row dense>
            <v-col>
              <v-select
                label="Modelos de laudo"
                v-model="appraisalTemplate"
                :items="appraisals"
                item-text="title"
                outlined
                hide-details
                dense
                background-color="white"
                @change="changeAppraisalTemplate"
                return-object
                :disabled="disabledCheck"
                no-data-text="Nenhum modelo disponível."
              />
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>

              <RichText
                v-model="appraisal"
                @change="setTyping(true)" 
                :disabled="disabledCheck"
                @input="handleDescriptionChange"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col>
          <v-row dense>
            <v-col class="text-h5 gray--text"> Termos de Consentimento </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <FileUpload
                ownerType="Exam::ConsementTerm"
                :ownerId="exam.consementTerm.id"
                v-if="exam"
                :disabled="signed"
              />
            </v-col>
          </v-row>
        </v-col>

        <v-col>
          <v-row dense>
            <v-col class="text-h5 gray--text"> Anexos </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <FileUpload
                ownerType="Exam::Attachment"
                :ownerId="exam.attachment.id"
                v-if="exam"
                :disabled="signed"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import DynamicFormCore from "@/components/dynamic_form/core.vue";
import SaveStatus from "@/components/ui/form/save_status/index.vue";

import { AutoSaveManager } from "@/services/AutoSaveManager/index.js";
import { applyAppraisalTemplate } from "./apply_appraisal_template";
import { normalizeString } from "@/helpers/string_helpers.js";

export default {
  name: "ExamForm",
  props: {
    attendance: Object,
    exam: Object,
    examLoading: Boolean,
    attendanceEsigned: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    DynamicFormCore,
    SaveStatus,
  },
  data() {
    return {
      typing: false, 
      examTemplates: [],
      users: [],
      examTemplate: null,
      updatedAt: null,
      appraisal: "",
      executedAt: "",
      requester: "",
      performer: "",
      assistants: [],
      appraisalTemplate: "",
      saveManager: null,
    };
  },
  created() {
    this.saveManager = new AutoSaveManager({
      formName: "Exame",
      beforeSave: () => this.beforeSave(),
      onSave: () => this.onSave(),
      autoSaveTimeout: 1800000, // 15 minutes
      showUnsavedAlertOnLeave: !this.disabled,
    });
  }, 
  mounted() {
    this.refreshList();
    this.loadExam();
  },
  apollo: {
    examTemplates: {
      query: require("@/graphql/exam/exam_templates.gql"),
      update(data) {
        let exams = this.lodash.orderBy(
          data.examTemplates,
          [(e) => normalizeString(e.name)],
          ["asc"]
        );
        if (!this.signed) {
          exams = this.lodash.filter(exams, { visible: true });
        }

        return exams;
      },
      result() {
        this.loadExamTemplate();
      },
    },
    users: {
      query: require("@/graphql/users.gql"),
      result() {
        this.loadUsers();
      },
    },
  },
  computed: {
    disabledCheck() {
      return (
        this.exam.lastEsign.status !== "editable" ||
        this.disabled ||
        this.exam?.finished
      );
    },
    dynamicFormType() {
      if (this.examTemplate?.dynamicForm?.dynamicFormType) {
        return this.examTemplate.dynamicForm.dynamicFormType;
      }

      return null;
    },
    doctorUsers() {
      return this.lodash.orderBy(
        this.lodash.filter(this.users, {
          employee: { employeeType: "doctor" },
        }),
        [(e) => normalizeString(e.employee.name)],
        ["asc"]
      );
    },
    nurseUsers() {
      return this.lodash.orderBy(
        this.lodash.filter(this.users, {
          employee: { employeeType: "nurse" },
        }),
        [(e) => normalizeString(e.employee.name)],
        ["asc"]
      );
    },
    hasMedicalRequest() {
      if (this.exam?.medicalRequest?.id) {
        return true;
      }

      return false;
    },
    signed() {
      if (this.disabled) {
        return this.disabled;
      }

      if (this.exam) {
        return (
          this.exam.lastEsign.status !== "editable" || this.attendanceEsigned
        );
      }

      return this.attendanceEsigned;
    },
    attendanceId() {    
      return this.exam?.attendance.id || this.attendance?.id
    },
    appraisals() {
      if (this.examTemplate?.examAppraisals) {
        return this.lodash.orderBy(
          this.examTemplate.examAppraisals,
          [(e) => normalizeString(e.title)],
          ["asc"]
        );
      }

      return [];
    },
  },
  methods: {
    setTyping(status){
      this.handleDescriptionChange();
      this.typing = status;
    },

    refreshList() {
      this.$apollo.queries.examTemplates.refetch();
    },
    changeAppraisalTemplate() {
      this.appraisal = applyAppraisalTemplate(
        this.appraisal,
        this.appraisalTemplate.description
      );

      //this.saveManager.dispatchAutoSave();
      this.appraisalTemplate = {};
    },
    loadExam() {
      this.appraisal = this.exam.appraisal;
      this.updatedAt = this.exam.updatedAt;

      this.executedAt = this.$moment(new Date(this.exam.createdAt)).format(
        "YYYY-MM-DDTHH:mm"
      );

      this.loadExamTemplate();
      this.loadUsers();

      if (!this.exam.executedAt) {
        this.executedAt = this.$moment(new Date()).format("YYYY-MM-DDTHH:mm");
      }
    },
    loadExamTemplate() {
      if (this.exam?.examTemplate?.id) {
        this.examTemplate = this.lodash.find(this.examTemplates, {
          id: this.exam.examTemplate.id,
        });
      }
    },
    loadUsers() {
      if (this.exam?.requester?.id) {
        this.requester = this.lodash.find(this.doctorUsers, {
          id: this.exam.requester.id,
        });
      }

      if (this.exam?.performer?.id) {
        this.performer = this.lodash.find(this.doctorUsers, {
          id: this.exam.performer.id,
        });
      }

      if (this.exam) {
        this.assistants = this.lodash.filter(
          this.nurseUsers,
          function(e) {
            return this.lodash.map(this.exam.assistants, "id").includes(e.id);
          }.bind(this)
        );
      }
    },
    beforeSave() {
      return !this.signed;
    },
    async onSave() {
      this.typing = false;
      const { data } = await this.apolloMutate({
        mutation: require("@/graphql/exam/save_exam.gql"),
        variables: {
          attendanceId: this.attendanceId,
          examId: this.lodash.get(this.exam, "id", ""),
          examTemplateId: this.lodash.get(this.examTemplate, "id", ""),
          appraisal: this.appraisal,
          executedAt: this.executedAt,
          requesterId: this.lodash.get(this.requester, "id", ""),
          performerId: this.lodash.get(this.performer, "id", ""),
          assistantIds: this.lodash.map(this.assistants, "id"),
        },
      });

      this.updatedAt = data.saveExam.updatedAt;
    },
   handleDescriptionChange() {
    this.saveManager.dispatchAutoSave();
   },
  },
};
</script>

<style lang="scss" scoped></style>
